import React, { useEffect, useState } from "react";
import { adminApi } from "../../lib/adminApi";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import { Trash2, Ban, CheckCircle2, Loader2 } from "lucide-react";
import { toast } from "sonner";

const AdminUsers = ({ onAuthError }) => {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [confirmId, setConfirmId] = useState("");

  const load = () => {
    adminApi.get(`/admin/users?q=${encodeURIComponent(q)}`).then((r) => setUsers(r.data)).catch(onAuthError);
  };
  useEffect(() => { load(); }, []);

  const toggleDisabled = async (u) => {
    try {
      await adminApi.patch(`/admin/users/${u.user_id}`, { disabled: !u.disabled });
      toast.success(u.disabled ? "User enabled" : "User disabled");
      load();
    } catch (e) { onAuthError(e); toast.error("Action failed"); }
  };
  const remove = async (u) => {
    try {
      await adminApi.delete(`/admin/users/${u.user_id}`);
      toast.success("User deleted");
      setConfirmId("");
      load();
    } catch (e) { onAuthError(e); toast.error("Delete failed"); }
  };

  const changeMembership = async (u, tier) => {
    try {
      await adminApi.put(`/admin/users/${u.user_id}/membership`, { tier });
      toast.success(tier === "growth" ? "Growth activated for three calendar months" : "User moved to Free");
      load();
    } catch { toast.error("Could not update membership"); }
  };
  return (
    <div data-testid="admin-users">
      <div className="flex gap-2 mb-4">
        <Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="Search by name or email…" className="bg-white border-slate-300 text-slate-900 max-w-sm" data-testid="admin-users-search" />
        <Button onClick={load} data-testid="admin-users-search-button" className="bg-sky-600 hover:bg-sky-500 text-white">Search</Button>
      </div>
      {!users ? <div className="flex justify-center py-20 text-slate-500"><Loader2 className="w-5 h-5 animate-spin" /></div> : (
        <div className="rounded-2xl bg-white border border-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.04),0_10px_30px_-24px_rgba(15,23,42,0.22)] overflow-hidden">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <th className="px-4 py-3">User</th><th className="px-4 py-3">Resumes</th><th className="px-4 py-3">Generated</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
            </tr></thead>
            <tbody>
              {users.length === 0 && <tr><td colSpan="5" className="px-4 py-8 text-center text-slate-500">No users found.</td></tr>}
              {users.map((u) => (
                <tr key={u.user_id} className="border-b border-slate-200" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-4 py-3"><div className="font-medium text-slate-800">{u.name || "—"}</div><div className="text-xs text-slate-500">{u.email}</div><div className="mt-2 text-xs capitalize text-sky-700" data-testid={`user-membership-${u.user_id}`}>{u.membership?.tier || "free"}{u.membership?.expires_at && ` · until ${new Date(u.membership.expires_at).toLocaleDateString()}`}</div>{u.upgrade_requested_at && <p className="text-xs text-amber-700" data-testid={`user-upgrade-request-${u.user_id}`}>Growth requested</p>}<div className="flex flex-wrap gap-2 mt-2"><button onClick={() => changeMembership(u, "growth")} className="text-xs text-emerald-700" data-testid={`user-grant-growth-${u.user_id}`}>{u.membership?.tier === "growth" ? "Renew Growth (3 months)" : "Grant Growth (3 months)"}</button>{u.membership?.tier === "growth" && <button onClick={() => changeMembership(u, "free")} className="text-xs text-slate-500" data-testid={`user-set-free-${u.user_id}`}>Move to Free</button>}</div></td>
                  <td className="px-4 py-3 text-slate-700">{u.resumes}</td>
                  <td className="px-4 py-3 text-slate-700">{u.generated}</td>
                  <td className="px-4 py-3">{u.disabled ? <span className="text-rose-600 text-xs">Disabled</span> : <span className="text-emerald-600 text-xs">Active</span>}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <Button size="sm" variant="outline" onClick={() => toggleDisabled(u)} className="border-slate-300 text-slate-700 h-8" data-testid={`admin-user-toggle-${u.user_id}`}>
                        {u.disabled ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                      </Button>
                      {confirmId === u.user_id ? (
                        <Button size="sm" onClick={() => remove(u)} className="bg-rose-600 hover:bg-rose-500 text-white h-8" data-testid={`admin-user-confirm-delete-${u.user_id}`}>Confirm</Button>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setConfirmId(u.user_id)} className="border-rose-200 text-rose-600 hover:bg-rose-50 h-8" data-testid={`admin-user-delete-${u.user_id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminUsers;
